/**
 * React-intl message catalog keys — BCP‑47 tags matching the locale file names (`*.json`).
 * `whoami.locale` / `users.locale` may send full tags (e.g. `fr-FR`) or short codes (`fr`); aliases
 * resolve to the catalogs we ship.
 */
export const SUPPORTED_INTL_MESSAGE_LOCALES = [
  'en-US',
  'af-ZA',
  'am-ET',
  'ar',
  'az-AZ',
  'bg-BG',
  'bn-IN',
  'bs-BA',
  'ca',
  'cs-CZ',
  'da-DK',
  'de-DE',
  'el-GR',
  'en-GB',
  'es-ES',
  'es-MX',
  'et-EE',
  'fa-AF',
  'fa-IR',
  'fi-FI',
  'fr-CA',
  'fr-FR',
  'gu-IN',
  'ha',
  'he-IL',
  'hi-IN',
  'hr-HR',
  'ht-HT',
  'hu-HU',
  'hy-AM',
  'id-ID',
  'it-IT',
  'ja-JP',
  'ka-GE',
  'kk-KZ',
  'kn-IN',
  'ko-KR',
  'lt-LT',
  'lv-LV',
  'mk-MK',
  'ml-IN',
  'mn-MN',
  'mr-IN',
  'ms-MY',
  'nb-NO',
  'nl-NL',
  'pa-IN',
  'pl-PL',
  'ps',
  'pt-BR',
  'pt-PT',
  'ro-RO',
  'ru-RU',
  'si-LK',
  'sk-SK',
  'sl-SI',
  'so',
  'sq-AL',
  'sr-RS',
  'sv-SE',
  'sw',
  'ta-IN',
  'te-IN',
  'th-TH',
  'tl-PH',
  'tr-TR',
  'uk-UA',
  'ur',
  'uz-UZ',
  'vi-VN',
  'zh-CN',
  'zh-HK',
  'zh-TW',
] as const;

export type IntlMessageLocale = (typeof SUPPORTED_INTL_MESSAGE_LOCALES)[number];

export const DEFAULT_INTL_MESSAGE_LOCALE: IntlMessageLocale = 'en-US';

/** Languages written right to left; the document's direction follows them. */
export const RTL_INTL_MESSAGE_LOCALES: ReadonlySet<IntlMessageLocale> = new Set([
  'ar',
  'fa-AF',
  'fa-IR',
  'he-IL',
  'ps',
  'ur',
]);

/**
 * Lowercase normalized locale strings (`-` separators) → shipped catalog key.
 * Covers hire's short codes and common regional variants from browsers.
 */
const LOCALE_ALIASES: Record<string, IntlMessageLocale> = {
  af: 'af-ZA',
  am: 'am-ET',
  az: 'az-AZ',
  bg: 'bg-BG',
  bn: 'bn-IN',
  bs: 'bs-BA',
  cs: 'cs-CZ',
  da: 'da-DK',
  de: 'de-DE',
  'de-at': 'de-DE',
  'de-ch': 'de-DE',
  el: 'el-GR',
  en: 'en-US',
  'en-au': 'en-US',
  es: 'es-ES',
  'es-ar': 'es-ES',
  'es-co': 'es-ES',
  'es-us': 'es-ES',
  et: 'et-EE',
  fa: 'fa-IR',
  fi: 'fi-FI',
  fil: 'tl-PH',
  fr: 'fr-FR',
  'fr-be': 'fr-FR',
  'fr-ch': 'fr-FR',
  gu: 'gu-IN',
  he: 'he-IL',
  hi: 'hi-IN',
  hr: 'hr-HR',
  ht: 'ht-HT',
  hu: 'hu-HU',
  hy: 'hy-AM',
  id: 'id-ID',
  it: 'it-IT',
  'it-ch': 'it-IT',
  iw: 'he-IL',
  ja: 'ja-JP',
  ka: 'ka-GE',
  kk: 'kk-KZ',
  kn: 'kn-IN',
  ko: 'ko-KR',
  lt: 'lt-LT',
  lv: 'lv-LV',
  mk: 'mk-MK',
  ml: 'ml-IN',
  mn: 'mn-MN',
  mr: 'mr-IN',
  ms: 'ms-MY',
  nb: 'nb-NO',
  nl: 'nl-NL',
  nn: 'nb-NO',
  no: 'nb-NO',
  pa: 'pa-IN',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  si: 'si-LK',
  sk: 'sk-SK',
  sl: 'sl-SI',
  sq: 'sq-AL',
  sr: 'sr-RS',
  sv: 'sv-SE',
  ta: 'ta-IN',
  te: 'te-IN',
  th: 'th-TH',
  tl: 'tl-PH',
  tr: 'tr-TR',
  uk: 'uk-UA',
  uz: 'uz-UZ',
  vi: 'vi-VN',
  zh: 'zh-CN',
};

function normalizeLocaleString(raw: string): string {
  return raw.trim().replace(/_/g, '-');
}

/** Maps a raw locale string to the catalog key `loadMessages` reads. */
export function resolveIntlMessageLocale(
  raw: string | undefined,
  fallback: IntlMessageLocale,
): IntlMessageLocale {
  if (!raw) return fallback;

  const normalized = normalizeLocaleString(raw);
  const lower = normalized.toLowerCase();

  const exactKey = SUPPORTED_INTL_MESSAGE_LOCALES.find(
    k => k.toLowerCase() === lower,
  );
  if (exactKey) return exactKey;

  if (LOCALE_ALIASES[lower]) {
    return LOCALE_ALIASES[lower];
  }

  const primary = lower.split('-')[0] ?? lower;
  if (LOCALE_ALIASES[primary]) {
    return LOCALE_ALIASES[primary];
  }

  return fallback;
}
